// BFN Calculator keep-alive policy.
//
// The calculator is a ~5.5 MB monolithic SPA that takes ~6-7 s of main-thread
// work to boot even fully cached (measured on staging 2026-08-01). Keeping the
// iframe mounted-but-hidden after the first open makes every later open
// instant — but a hidden heavy iframe holds real renderer memory, which is
// exactly what older / low-RAM devices cannot spare. This module is the single
// place that decides who gets keep-alive; everyone else keeps today's
// unmount-on-close behavior.

/**
 * True when this device can afford to keep the hidden calculator iframe
 * mounted. Deliberately conservative:
 *
 * - iOS is always excluded: Safari exposes no memory signal and its jetsam
 *   process killer evicts memory-heavy tabs aggressively — a reload of the
 *   whole messenger costs far more than the ~7 s calculator reopen it would
 *   save. (iPadOS 13+ masquerades as "MacIntel" desktop — the maxTouchPoints
 *   check catches it.)
 * - Where Chrome's `navigator.deviceMemory` exists, require >= 4 GB, which
 *   screens out exactly the older Android/desktop hardware the concern is
 *   about (the API reports at most 8, floored to 0.25/0.5/1/2/4/8).
 * - No signal at all (desktop Firefox/Safari) => assume capable; low-RAM
 *   devices in that population are overwhelmingly iOS, already excluded.
 */
export function bfnKeepAliveCapable(): boolean {
  const nav = navigator as Navigator & { deviceMemory?: number };
  const isIOS =
    /iPad|iPhone|iPod/.test(nav.userAgent) ||
    (nav.platform === "MacIntel" && nav.maxTouchPoints > 1);
  if (isIOS) {
    return false;
  }
  if (typeof nav.deviceMemory === "number") {
    return nav.deviceMemory >= 4;
  }
  return true;
}

// How long a hidden calculator stays mounted before it is dropped anyway.
// Long enough to cover the common "check a chat, come right back" hop;
// short enough that the memory is never held for a whole session spent
// elsewhere. The next open after an idle unmount is simply today's cold open.
export const BFN_IDLE_UNMOUNT_MS = 10 * 60_000;
